import { webToApp } from '../../mobileApp/appInterface';
import {
  errorMessages,
  isTLabelStatusComplete, routeNames, tLabelDataDownloadEnum, tLabelDiscoveryEnum, tLabelStatuses,
} from '../../constants';
import { createNotification, notificationTypes } from '../../helpers/notification-helper';

const getDefaultState = () => ({
  discoveryStatus: null,
  downloadStatuses: {}, // keyed by bleId
  downloadStatusesWatchId: 0, // to force reactivity

  /** <bleId>: {
   *    bleId: number,
   *    asset: {id, name},
   *    workspace: {id},
   *    discoveredAt: Date,
   *    completedAt: Date, // if downloaded
   *    readings: [],
   *    status: 'discovered',
   *    currentTemperature: null, // if available
   *    failureReason: 'some text' // optional, if download failed
   * },
  */
  labels: {}, // keyed by bleId
  labelsStatusesWatchId: 0, // to force reactivity
  numDiscoveredLabels: 0,
  bulkDownloadStartedAt: null,
  isRetryRunning: false, // to prevent multiple retries
  timeoutId: null, // for bulk download timeout

  // clean up
  cleanUpIntervalId: null, // to periodically clean up completed labels
  cleanUpLabelsCount: 0, // to track number of labels cleaned up

  // expire discovered labels
  expireDiscoveredIntervalId: null,
});

const T_LABEL_REDISCOVER_TIMEOUT = (process.env.VUE_APP_T_LABEL_REDISCOVER_TIMEOUT_SECS
  || 60 * 10) * 1000; // 10 minutes
const T_LABEL_DISCOVERY_TIMEOUT = 2 * T_LABEL_REDISCOVER_TIMEOUT;
const T_LABEL_DOWNLOAD_TIMEOUT_SECS = process.env
  .VUE_APP_T_LABEL_DOWNLOAD_TIMEOUT_SECS || 60; // 1 minute
const T_LABEL_COMPLETED_MAX_AGE_SECS = process.env.VUE_APP_T_LABEL_COMPLETED_MAX_AGE_SECS
  || 60 * 30; // 30 mins

const TEN_MINS_AS_MS = 10 * 60 * 1000; // 10 minutes
const ONE_DAY_AS_MS = 24 * 60 * 60 * 1000; // 1 day

/** * Download helper functions * */
function assetHasRecentReadings(asset) {
  if (!asset || !asset.temperatures || !Array.isArray(asset.temperatures.items)
    || !asset.labelAddedOn) {
    return false;
  }

  const now = new Date();
  // how many 10-min intervals have passed since label was activated?
  const labelAddedOn = new Date(asset.labelAddedOn);
  const numberOrReadingsExpected = Math.floor(Math.min(
    now - labelAddedOn,
    ONE_DAY_AS_MS,
  ) / TEN_MINS_AS_MS);

  if (numberOrReadingsExpected <= 0) {
    // return true as there are no readings expected
    return true;
  }

  // how many readings are there?
  // - check that there is at least 1 reading in the previous
  //    <numberOrReadingsExpected> 10-min intervals
  const recentReadings = asset.temperatures.items.filter((reading) => {
    const readingDate = new Date(reading.timestamp);
    return readingDate >= labelAddedOn && readingDate <= now;
  });

  const recentReadingsWithIntervals = recentReadings
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  // is there at least one missing reading for the previous 10-min intervals in the last 24 hours?
  //  - excluding the most recent 10-mins (as the latest real-time reading may affect it)
  for (let i = TEN_MINS_AS_MS;
    i <= TEN_MINS_AS_MS * (numberOrReadingsExpected + 1);
    i += TEN_MINS_AS_MS) {
    const expectedInterval = new Date(now - i);
    const readingInExpectedInterval = recentReadingsWithIntervals.find((reading) => {
      const readingDate = new Date(reading.timestamp);
      return readingDate >= expectedInterval
        && readingDate < new Date(expectedInterval.getTime() + TEN_MINS_AS_MS);
    });
    if (!readingInExpectedInterval) {
      return false;
    }
  }

  return true;
}

function cleanUpPreviouslyCompletedLabels(context) {
  // remove any labels that have been downloaded and are older than T_LABEL_COMPLETED_MAX_AGE_SECS
  const now = new Date();
  const labelsToRemove = Object.values(context.state.labels)
    .filter((label) => isTLabelStatusComplete(label.status)
      && label.completedAt
      && (now - new Date(label.completedAt)) > T_LABEL_COMPLETED_MAX_AGE_SECS * 1000);

  if (labelsToRemove.length > 0) {
    labelsToRemove.forEach((label) => {
      context.commit('REMOVE_T_LABEL', label.bleId);
    });

    context.commit('INCREMENT_CLEAN_UP_LABELS_COUNT', labelsToRemove.length);
  }
}

function cleanUpExpiredDiscoveredLabels(context) {
  // remove any discovered labels that have not been downloaded within T_LABEL_DISCOVERY_TIMEOUT
  const now = new Date();
  const labelsToRemove = Object.values(context.state.labels)
    .filter((label) => label.status === tLabelStatuses.discovered
      && (now - new Date(label.discoveredAt)) > T_LABEL_DISCOVERY_TIMEOUT);

  if (labelsToRemove.length > 0) {
    labelsToRemove.forEach((label) => {
      context.commit('REMOVE_T_LABEL', label.bleId);
    });
  }
}

function startDownloadForNextDiscoveredLabel(context) {
  const anyIncomplete = context.getters.discoveredTLabelAssets
    .some((label) => !isTLabelStatusComplete(label.status));
  // bulkDownloadStartedAt is null when download is stopped / comes to an end
  if (!context.state.isRetryRunning
    && context.state.bulkDownloadStartedAt
    && context.getters.hasDiscoveredTLabelAssets) {
    const firstDiscoveredLabel = context.getters.discoveredTLabelAssets
      .find((label) => label.status === tLabelStatuses.discovered);
    // start download for the first discovered label
    context.dispatch('startDownload', { bleIds: [firstDiscoveredLabel.bleId], timeout: T_LABEL_DOWNLOAD_TIMEOUT_SECS });

    const timeoutId = setTimeout(() => {
      // check if the label is still in the downloading state
      if (context.state.labels[firstDiscoveredLabel.bleId]
        && context.state.labels[firstDiscoveredLabel.bleId].status === tLabelStatuses.downloading) {
        // set status to failed
        context.commit('SET_LABEL_STATUS', {
          bleId: firstDiscoveredLabel.bleId,
          status: tLabelStatuses.failed,
          failureReason: 'Download timed out',
        });
        // start download for the next discovered label
        startDownloadForNextDiscoveredLabel(context);
      }
    }, T_LABEL_DOWNLOAD_TIMEOUT_SECS * 1000);
    context.commit('START_DOWNLOAD_TIMEOUT', timeoutId);
  } else if (!anyIncomplete) {
    // reset timeout
    context.commit('STOP_DOWNLOAD_TIMEOUT');

    // notify user that download has ended
    const numberDownloaded = context.getters.downloadedLabelsCount;

    if (numberDownloaded > 0) {
      const numberFailed = context.getters.discoveredTLabelAssets
        .filter((label) => label.status === tLabelStatuses.failed
        && label.completedAt >= context.state.bulkDownloadStartedAt).length;

      const isPlural = numberDownloaded > 1;
      let message = `Temperature download complete. ${numberDownloaded} label${isPlural ? 's' : ''} downloaded`;

      if (numberFailed > 0) {
        message += `, ${numberFailed} failed`;
      }

      context.commit('notifications/ADD_NOTIFICATION', createNotification(
        message,
        notificationTypes.tLabelDownloadComplete,
        null,
        numberFailed > 0,
        numberFailed > 0,
        [
          routeNames.temperature.download,
          routeNames.assets.downloadTemperature,
        ],
      ), { root: true });
    }

    context.commit('END_BULK_DOWNLOAD');
  }

  // start clean up interval if not already started
  if (!context.state.cleanUpIntervalId) {
    const cleanUpIntervalId = setInterval(() => {
      cleanUpPreviouslyCompletedLabels(context);
    }, Math.max(Math.round(T_LABEL_COMPLETED_MAX_AGE_SECS / 3), 10) * 1000);
    context.commit('SET_CLEAN_UP_INTERVAL_ID', cleanUpIntervalId);
  }
}

function resetLabelsStatuses(context) {
  // remove any 'downloading' statuses
  const labels = context.getters.discoveredTLabelAssets
    .filter((label) => label.status === tLabelStatuses.downloading);
  if (labels.length > 0) {
    labels.forEach((label) => {
      context.commit('SET_LABEL_STATUS', {
        bleId: label.bleId,
        status: tLabelStatuses.discovered,
      });
    });
  }
}

export default {
  namespaced: true,
  state: getDefaultState(),
  mutations: {
    RESET_STATE(state) {
      Object.assign(state, getDefaultState());
    },
    SET_DISCOVERY_STATUS(state, status) { state.discoveryStatus = status; },
    RESET_DISCOVERY_STATUS(state) { state.discoveryStatus = null; },
    SET_DOWNLOAD_STATUS(state, statusData) {
      if (statusData.bleId) {
        state.downloadStatuses[statusData.bleId] = statusData;
        state.downloadStatusesWatchId += 1;
      }
    },
    SET_LABEL_STATUS(state, { bleId, status, failureReason = null }) {
      if (state.labels[bleId]) {
        state.labels[bleId].status = status;
        state.labelsStatusesWatchId += 1;
        if (failureReason) {
          state.labels[bleId].failureReason = failureReason;
        }

        if (isTLabelStatusComplete(status)) {
          state.labels[bleId].completedAt = new Date();
        } else {
          state.labels[bleId].completedAt = null;
        }
      }
    },
    SET_LABEL_READINGS(state, { bleId, readings }) {
      if (state.labels[bleId]) {
        state.labels[bleId].readings = readings;
      }
    },
    RESET_DOWNLOAD_STATUS_FOR_LABELS(state, bleIds) {
      bleIds.forEach((bleId) => {
        delete state.downloadStatuses[bleId];
      });
      state.downloadStatusesWatchId += 1;
    },
    DISCOVER_LABEL(state, {
      bleId, asset, workspace, currentTemperature = null,
    }) {
      state.labels[bleId] = {
        bleId,
        asset,
        workspace,
        discoveredAt: new Date(),
        completedAt: null,
        readings: [],
        currentTemperature,
        status: tLabelStatuses.discovered,
      };
      state.numDiscoveredLabels += 1;
    },
    REMOVE_T_LABEL(state, bleId) {
      if (state.labels[bleId]) {
        delete state.labels[bleId];
      }
      state.labelsStatusesWatchId += 1;
    },
    SET_CLEAN_UP_INTERVAL_ID(state, intervalId) {
      if (state.cleanUpIntervalId) {
        clearInterval(state.cleanUpIntervalId);
      }
      state.cleanUpIntervalId = intervalId;
    },
    INCREMENT_CLEAN_UP_LABELS_COUNT(state, incrementBy = 1) {
      state.cleanUpLabelsCount += incrementBy;
    },
    RESET_CLEAN_UP_LABELS_COUNT(state) {
      state.cleanUpLabelsCount = 0;
    },
    SET_EXPIRE_DISCOVERED_INTERVAL_ID(state, intervalId) {
      if (state.expireDiscoveredIntervalId) {
        clearInterval(state.expireDiscoveredIntervalId);
      }
      state.expireDiscoveredIntervalId = intervalId;
    },
    START_DOWNLOAD_TIMEOUT(state, timeoutId) {
      if (state.timeoutId) {
        clearTimeout(state.timeoutId);
      }
      state.timeoutId = timeoutId;
    },
    STOP_DOWNLOAD_TIMEOUT(state) {
      if (state.timeoutId) {
        clearTimeout(state.timeoutId);
      }
      state.timeoutId = null;
    },
    START_BULK_DOWNLOAD(state) {
      state.bulkDownloadStartedAt = new Date();
    },
    END_BULK_DOWNLOAD(state) {
      state.bulkDownloadStartedAt = null;
      state.isRetryRunning = false; // reset retry state
    },
    START_RETRY(state) {
      state.isRetryRunning = true;
    },
  },
  actions: {
    // webToApp...
    startDiscovery(context) {
      context.commit('RESET_DISCOVERY_STATUS');
      webToApp.tLabel.startDiscovery();
    },
    stopDiscovery() {
      webToApp.tLabel.stopDiscovery();
    },
    startDownload(context, { bleIds, timeout }) {
      context.commit('RESET_DOWNLOAD_STATUS_FOR_LABELS', bleIds);
      bleIds.forEach((bleId) => {
        context.commit('SET_LABEL_STATUS', {
          bleId,
          status: tLabelStatuses.downloading,
        });
      });
      webToApp.tLabel.startDownload(bleIds, timeout);
    },
    stopDownload(context, bleIds) {
      webToApp.tLabel.stopDownload(bleIds);
      context.commit('RESET_DOWNLOAD_STATUS_FOR_LABELS', bleIds);

      const labels = context.getters.discoveredTLabelAssets
        .filter((label) => bleIds.includes(label.bleId));
      labels.forEach((label) => {
        context.commit('SET_LABEL_STATUS', {
          bleId: label.bleId,
          status: tLabelStatuses.discovered,
        });
      });
    },
    // appToWeb...
    setDiscoveryStatus(context, status) {
      switch (status.status) {
        case tLabelDiscoveryEnum.bluetoothUnsupported:
          context.commit(
            'notifications/ADD_NOTIFICATION',
            createNotification('This device does not support Bluetooth', notificationTypes.bluetoothUnsupported, null, true, true),
            { root: true },
          );
          break;
        case tLabelDiscoveryEnum.bluetoothOff:
          context.commit(
            'notifications/ADD_NOTIFICATION',
            createNotification('Please switch Bluetooth on', notificationTypes.bluetoothOff, null, true, true),
            { root: true },
          );
          break;
        case tLabelDiscoveryEnum.bluetoothUnauthorized:
          context.commit(
            'notifications/ADD_NOTIFICATION',
            createNotification('Please grant permission to use Bluetooth', notificationTypes.bluetoothUnauthorized, null, true, true),
            { root: true },
          );
          break;
        case tLabelDiscoveryEnum.error:
          context.commit(
            'notifications/ADD_NOTIFICATION',
            createNotification(`An error has occurred while scanning for labels${status.message ? `. ${status.message}` : ''}`, notificationTypes.error, null, true, true),
            { root: true },
          );
          break;

        default:
          // nothing
      }

      context.commit('SET_DISCOVERY_STATUS', status);
    },
    setDownloadStatus(context, statusData) {
      context.commit('SET_DOWNLOAD_STATUS', statusData);

      if ([
        tLabelDataDownloadEnum.error,
        tLabelDataDownloadEnum.timeout,
      ].includes(statusData.status)) {
        context.commit('SET_LABEL_STATUS', {
          bleId: statusData.bleId,
          status: tLabelStatuses.failed,
          failureReason: statusData.status === tLabelDataDownloadEnum.timeout
            ? 'Download timed out'
            : statusData.message || 'An error occurred while downloading data',
        });

        // check for next label to download
        startDownloadForNextDiscoveredLabel(context);
      }
    },
    async setData(context, data) {
      context.commit('SET_DOWNLOAD_STATUS', {
        bleId: data.bleId,
        status: tLabelDataDownloadEnum.complete,
      });
      context.dispatch('assets/addLabelTemperatureReadings', { bleId: data.bleId, readings: data.readings }, { root: true });

      // update label status to downloaded
      context.commit('SET_LABEL_STATUS', {
        bleId: data.bleId,
        status: tLabelStatuses.downloaded,
      });
      context.commit('SET_LABEL_READINGS', {
        bleId: data.bleId,
        readings: data.readings,
      });

      // check for next label to download
      startDownloadForNextDiscoveredLabel(context);
    },
    async discoverLabel(context, { bleId, degreesC }) {
      let asset = null;
      let shouldSaveLabel = false;
      try {
        // if label already exists, skip discovery
        // - or has expired
        if (!context.state.labels[bleId]
          || (context.state.labels[bleId].status === tLabelStatuses.discovered
              && new Date() - context.state.labels[bleId]
                .discoveredAt) > T_LABEL_REDISCOVER_TIMEOUT) {
          shouldSaveLabel = true;
          // get asset for the label
          asset = await context.dispatch(
            'assets/getAssetByBleId',
            {
              bleId, skipRedirect: true, skipLoading: true, includeLabelData: true,
            },
            { root: true },
          );

          // look at latest historical fetch / temperature readings
          // to determine whether to add to list
          // - can do this by checking if asset.temperatures.items.length > 1
          // && whether the latest 3 readings are within 30 minutes
          if (assetHasRecentReadings(asset)) {
            shouldSaveLabel = false;
          }
        }
      } catch (error) {
        if (!error.message.includes(`No asset found with bleId: ${bleId}`)
           && !error.message.includes(errorMessages.noPermission)
        ) {
          context.commit(
            'notifications/ADD_NOTIFICATION',
            createNotification(`An error occurred while discovering label ${bleId}: ${error.message}`, notificationTypes.error, null, true),
            { root: true },
          );
        }
      } finally {
        if (shouldSaveLabel) {
          let currentTemperature = degreesC;
          if (currentTemperature === null
            && asset
            && asset.temperatures
            && Array.isArray(asset.temperatures.items)
            && asset.temperatures.items.length > 0) {
            currentTemperature = asset.temperatures.items[0].degreesCelsius;
          }

          context.commit('DISCOVER_LABEL', {
            bleId,
            asset: asset ? { id: asset.id, name: asset.name } : null,
            workspace: asset ? { id: asset.workspaceId || asset.organisationId } : null,
            currentTemperature,
          });

          // start disovered label clean up if not already started
          if (!context.state.expireDiscoveredIntervalId) {
            const expireDiscoveredIntervalId = setInterval(() => {
              cleanUpExpiredDiscoveredLabels(context);
            }, Math.max(Math.round(T_LABEL_DISCOVERY_TIMEOUT / 3), 5000));
            context.commit('SET_EXPIRE_DISCOVERED_INTERVAL_ID', expireDiscoveredIntervalId);
          }
        }
      }
    },

    // User actions
    startBulkDownload(context) {
      context.commit('START_BULK_DOWNLOAD');
      try {
        resetLabelsStatuses(context);

        startDownloadForNextDiscoveredLabel(context);
      } catch (error) {
        console.error('Error starting bulk download', error);
        context.commit('notifications/ADD_NOTIFICATION', createNotification(`An error occurred while starting bulk download: ${error.message}`, notificationTypes.error, null, true), { root: true });
      }
    },
    stopBulkDownload(context) {
      try {
        resetLabelsStatuses(context);

        // reset timeout
        context.commit('STOP_DOWNLOAD_TIMEOUT');

        context.commit('END_BULK_DOWNLOAD');
      } catch (error) {
        console.error('Error stopping bulk download', error);
        context.commit('notifications/ADD_NOTIFICATION', createNotification(`An error occurred while cancelling bulk download: ${error.message}`, notificationTypes.error, null, true), { root: true });
      }
    },
    retryLabelDownload(context, bleId) {
      if (!context.state.isRetryRunning) {
        context.commit('START_RETRY');
        context.commit('START_BULK_DOWNLOAD'); // to start the bulk download process
        context.dispatch('startDownload', { bleIds: [bleId], timeout: T_LABEL_DOWNLOAD_TIMEOUT_SECS });
      }
    },
  },
  getters: {
    // display banner is discovered T labels in selected team
    hasDiscoveredTLabelAssets(state, _, rootState) {
      return state.numDiscoveredLabels > 0 // to force reactivity
        && state.labelsStatusesWatchId >= 0 // to force reactivity
        && Object.values(state.labels)
          .some((label) => label.status === tLabelStatuses.discovered
            && !!label.asset
            && !!label.workspace
            && label.workspace.id === rootState.teams.selectedTeam.id);
    },
    discoveredTLabelAssets(state, _, rootState) {
      return (state.numDiscoveredLabels > 0 // to force reactivity
        && state.labelsStatusesWatchId >= 0 // to force reactivity
        && state.downloadStatusesWatchId >= 0 // to force reactivity
        ? Object.values(state.labels)
        : [])
        .filter((label) => !!label.asset
        && !!label.workspace
        && label.workspace.id === rootState.teams.selectedTeam.id)
        // first discovered, then by asset name
        .sort((a, b) => {
          if (a.discoveredAt < b.discoveredAt) return -1;
          if (a.discoveredAt > b.discoveredAt) return 1;
          if (a.asset.name < b.asset.name) return -1;
          if (a.asset.name > b.asset.name) return 1;
          return 0;
        });
    },
    bulkDownloadInProgress(state) {
      return !!state.bulkDownloadStartedAt
        && Object.values(state.labels).some((label) => label.status === tLabelStatuses.downloading);
    },
    downloadedLabelsCount(state, getters) {
      return state.labelsStatusesWatchId >= 0 // to ensure reactivity
        && getters.discoveredTLabelAssets.filter((d) => isTLabelStatusComplete(d.status)).length;
    },
    failureCount(state, getters) {
      return state.labelsStatusesWatchId >= 0 // to ensure reactivity
        && getters.discoveredTLabelAssets.filter((d) => d.status === tLabelStatuses.failed).length;
    },
  },
};
